<template>
  <div class="flex items-center">
    <vue-element-loading
      :active="fetchingContactMatchCount"
      color="rgba(var(--vs-primary), 1)"
      spinner="mini-spinner"/>

    <h1
      v-if="!hideCount"
      class="text-5xl text-primary font-bold d-inline-block">
      {{ contactsMatchCount }}
    </h1>

    <slot>
      <span class="ml-3">
        <a
          v-if="showContactsListLink && contactsMatchCount > 0"
          href="#"
          :class="contactsListLinkClasses"
          @click.prevent="activeModalViewContacts = true"
        >
          {{ countMessage || defaultContactsMatchMessage }}
        </a>
        <template v-else>
          {{ countMessage || defaultContactsMatchMessage }}
        </template>

        <template v-if="manageContactsLinks && contactsMatchCount === 0">
          <a
            href="#"
            class="link-popup"
            @click.prevent="$emit('create-contact')"
          >
            {{ $t('CreateFirstContactLink') }}
          </a> {{ $t('$General.Or') | lowercase }}
          <router-link
            to="/contacts"
            href="#"
            class="link-cta"
          >
            {{ $t('ManageYourContactsLink') }}
          </router-link>
        </template>
      </span>
    </slot>

    <feather-icon
      v-if="showRefresh"
      icon='RefreshCwIcon'
      svgClasses='h-5 w-5'
      class='text-primary cursor-pointer ml-3'
      @click="refreshCountContactsSegments"/>

    <vs-popup
      :title="$t('ViewContactsModalTitle')"
      :fullscreen="true"
      :active.sync="activeModalViewContacts">
      <transition name="zoom-fade">
        <contact-list
          v-if="activeModalViewContacts"
          :initial-filter-match="filtersMatch"
          :initial-filters="getMappedFilters(false)"
          :marketing-status-filter-options="marketingStatusFilterOptions"
          :readonly-list="true"
          :hide-actions-column="true"
          :selected-from-campaign="true"
        />
      </transition>
    </vs-popup>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import enums from '@/enums';

/**
 * Show numbers of contacts that match with segment filters
 *
 * @module views/modules/segment/create-or-edit/SegmentListCreateOrEditContactsMatchCount
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 * @vue-prop {string} filtersMatch - filters match from segment
 * @vue-prop {boolean} isThereAnyValidFilter - indicate if exist any valid filter
 * @vue-prop {Function} getValidFilters - function to get valid filters to get contacts
 * @vue-data {number} [contactsMatchCount=0] - number of contacts that match with segment filters
 * @vue-data {boolean} [fetchingContactMatchCount=false] - indicate if component is fetching data
 * @vue-data {string} [lastFiltersQueried=''] - last filters queried in json formatted
 * @vue-event {void} refreshCountContactsSegments - fetch & refresh count of contacts
 */
export default {
  name: 'SegmentListCreateOrEditContactsMatchCount',
  components: {
    ContactList: () => import('@/views/modules/contacts/ContactList.vue'),
  },
  i18n: {
    messages: {
      en: {
        ContactsMatchMsg: 'No contacts match the filters | contact matches filters | contacts match filters',
        ContactsMatchMsgMarketing: 'No subscribed contacts match the filters | subscribed contact matches filters | subscribed contacts match filters',
        CreateFirstContactLink: 'Create your first contact',
        ManageYourContactsLink: 'Manage your contacts',
        ViewContactsModalTitle: 'Contacts that match filters',
      },
    },
  },
  props: {
    filtersMatch: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(enums.AppFilter.FilterMathType).includes(value);
      },
    },
    isThereAnyValidFilter: {
      type: Boolean,
      required: true,
    },
    getValidFilters: {
      type: Function,
      required: true,
    },
    showRefresh: {
      type: Boolean,
      required: false,
      default: true,
    },
    selectedFromCampaign: {
      type: Boolean,
      required: false,
      default: false,
    },
    campaignMessageType: {
      type: String,
      required: false,
      validator(type) {
        return Object.values(enums.Campaign.MessageType).includes(type);
      },
    },
    countMessage: {
      type: String,
      required: false,
      default: '',
    },
    hideCount: {
      type: Boolean,
      required: false,
      default: false,
    },
    showContactsListLink: {
      type: Boolean,
      required: false,
      default: false,
    },
    manageContactsLinks: {
      type: Boolean,
      required: false,
      default: false,
    },
    contactsListLinkClasses: {
      type: String,
      required: false,
      default: 'link-popup',
    },
  },
  data() {
    return {
      contactsMatchCount: 0,
      fetchingContactMatchCount: false,
      lastFiltersQueried: '',

      activeModalViewContacts: false,
      filtersMatchToViewContacts: this.filtersMatch,
    };
  },
  computed: {
    marketingStatusFilterOptions() {
      const marketingStates = [
        this.$enums.Contact.MarketingStatus.SUBSCRIBED,
      ];

      return this.campaignMessageType
      === this.$enums.Campaign.MessageType.TRANSACTIONAL || this.campaignMessageType === undefined
        ? [
          ...marketingStates,
          this.$enums.Contact.MarketingStatus.UNSUBSCRIBED,
        ]
        : marketingStates;
    },
    marketingStatusFilter() {
      return {
        filterInner: 'value',
        filterType: this.$enums.AppFilterType.ENUM,
        type: this.$enums.AppFilter.FilterType.Enum.Type.ONE,
        filter: this.marketingStatusFilterOptions,
      };
    },
    defaultContactsMatchMessage() {
      if (this.campaignMessageType === this.$enums.Campaign.MessageType.MARKETING) {
        return this.$tc('ContactsMatchMsgMarketing', this.contactsMatchCount);
      }
      return this.$tc('ContactsMatchMsg', this.contactsMatchCount);
    },
  },
  watch: {
    isThereAnyValidFilter() {
      this.refreshCountContactsSegments();
    },
    filtersMatch() {
      this.refreshCountContactsSegments();
    },
    contactsMatchCount(newVal) {
      this.$emit('count-changed', newVal);
    },
  },
  mounted() {
    this.refreshCountContactsSegments();
  },
  methods: {
    ...mapActions({
      getCountContacts: 'contact/fetchCountContactsFilters',
    }),
    checkIfItHasCategories(filters, validFilters) {


      const newFilters = { ...filters };

      Object.keys(validFilters).forEach((filterKey) => {
        if (validFilters[filterKey].filterType === this.$enums.AppFilterType.CATEGORIES
          && Array.isArray(validFilters[filterKey].filter)) {
          // Check if filter array is empty
          if (validFilters[filterKey].filter.length === 0) {
            return; // Skip empty filters
          }

          const filter = {
            ...validFilters[filterKey],
            filter: validFilters[filterKey].filter.map((value) => (typeof value === 'object' ? value.id : value)),
          };



          if ('segmentFilters' in newFilters) {
            newFilters.segmentFilters.filter[filterKey] = filter;
          } else {
            newFilters[filterKey] = filter;
          }
        }
      });

              return newFilters;
    },
    checkIfItHasTags(filters, validFilters) {
      const newFilters = { ...filters };

      if ('tags' in validFilters && Array.isArray(validFilters.tags.filter)) {
        const filter = {
          ...validFilters.tags,
          filter: validFilters.tags.filter.map((value) => (typeof value === 'object' ? value.id : value)),
        };

        if ('segmentFilters' in newFilters) {
          newFilters.segmentFilters.filter.tags = filter;
        } else {
          newFilters.tags = filter;
        }
      }

      return newFilters;
    },
    checkIfItsSelectedFromCampaign(filters, filtersMatch) {
      if (this.selectedFromCampaign) {
        return {
          filters: {
            marketingStatus: this.marketingStatusFilter,
            segmentFilters: {
              filterType: this.$enums.AppFilterType.CONDITIONAL,
              filtersMatch: this.filtersMatch,
              filter: filters,
            },
          },
          filtersMatch: this.$enums.AppFilterMathType.ALL,
        };
      }

      return {
        filters,
        filtersMatch,
      };
    },
    checkIfItHasPhoneInternational(filters, validFilters) {
      let newFilters = filters;

      if ('phoneInternational' in validFilters) {
        const phoneFilter = validFilters.phoneInternational;
        let processedFilter;

        // Handle multi-condition filters (condition1/condition2 structure)
        if (phoneFilter.condition1 && phoneFilter.condition2) {
          processedFilter = {
            filterType: this.$enums.AppFilterType.TEXT,
            condition1: {
              ...phoneFilter.condition1,
              filter: phoneFilter.condition1.filter ? phoneFilter.condition1.filter.replace(/ /g, '') : '',
            },
            condition2: {
              ...phoneFilter.condition2,
              filter: phoneFilter.condition2.filter ? phoneFilter.condition2.filter.replace(/ /g, '') : '',
            },
            operator: phoneFilter.operator,
          };
        }
        // Handle single condition filters (legacy structure)
        else if (phoneFilter.filter) {
          processedFilter = {
            ...phoneFilter,
            filter: phoneFilter.filter.replace(/ /g, ''),
          };
        }
        // Handle single condition filters with multi-condition structure but only condition1
        else if (phoneFilter.condition1) {
          processedFilter = {
            filterType: this.$enums.AppFilterType.TEXT,
            condition1: {
              ...phoneFilter.condition1,
              filter: phoneFilter.condition1.filter ? phoneFilter.condition1.filter.replace(/ /g, '') : '',
            },
            operator: phoneFilter.operator,
          };
        }
        // Default fallback
        else {
          processedFilter = phoneFilter;
        }

        newFilters = {
          ...newFilters,
          phoneNumberFilters: {
            filterType: this.$enums.AppFilterType.CONDITIONAL,
            filtersMatch: this.$enums.AppFilterMathType.ANY,
            filter: {
              phoneInternational: processedFilter,
              phoneInternationalSignificant: processedFilter,
              phoneInternationalSignificantComplete: processedFilter,
              phoneNational: processedFilter,
              phoneNationalSignificant: processedFilter,
              phoneSignificant: processedFilter,
            },
          },
        };

        if ('segmentFilters' in newFilters) {
          const { phoneInternational, ..._filters } = newFilters.segmentFilters.filter;
          newFilters.segmentFilters.filter = _filters;
        } else {
          const { phoneInternational, ..._filters } = newFilters;
          newFilters = _filters;
        }
      }

      return newFilters;
    },
    getMappedFilters(phone = true, tags = true, categories = true) {
      console.log('getMappedFilters', phone, tags, categories);
      const { filters } = this.getMappedFiltersAndFiltersMath(phone, tags, categories);

      return filters;
    },
    getMappedFiltersAndFiltersMath(phone = true, tags = true, categories = true) {
      const validFilters = this.getValidFilters(true);
      let filters = this.getValidFilters(true);
      const { filtersMatch } = this;

      filters = {
        ...filters,
        importing: {
          filterType: this.$enums.AppFilterType.BOOLEAN,
          filter: this.$enums.AppFilter.FilterType.Boolean.Values.NO,
        },
      };

      if (validFilters) {
        if (phone) filters = this.checkIfItHasPhoneInternational(filters, validFilters);
        if (tags) filters = this.checkIfItHasTags(filters, validFilters);
        if (categories) filters = this.checkIfItHasCategories(filters, validFilters);
      }

      return {
        filters,
        filtersMatch,
      };
    },
    async refreshCountContactsSegments() {
      if (this.isThereAnyValidFilter) {
        try {
          this.fetchingContactMatchCount = true;
          let { filters, filtersMatch } = this.getMappedFiltersAndFiltersMath();

          ({ filters, filtersMatch } = this.checkIfItsSelectedFromCampaign(filters, filtersMatch));

          // If filters is null (all filters were skipped), set count to 0
          if (!filters) {
            this.contactsMatchCount = 0;
            this.$emit('count-changed', this.contactsMatchCount);
            return;
          }

          // Create a more comprehensive cache key that includes filtersMatch
          const cacheKey = JSON.stringify({ filters, filtersMatch });

          if (filters && cacheKey !== this.lastFiltersQueried) {
            this.lastFiltersQueried = cacheKey;

            this.contactsMatchCount = await this.getCountContacts({
              filters,
              filtersMatch,
            });

            this.$emit('count-changed', this.contactsMatchCount);
          }
        } catch (e) {
          console.log(e);
        } finally {
          this.fetchingContactMatchCount = false;
        }
      }
    },
  },
};
</script>

<style scoped>

</style>
